import React, { useRef, memo } from 'react';
import classNames from 'classnames';

import CompanyContacts from '../../CompanyContacts';
import CompanyHarvcoTags from '../../Details/CompanyHarvcoTags';
import FormGroup from '../../../helpers/FormGroup';
import BaseInput from '../../../helpers/Input';
import BuyerDealNotes from '../BuyerDealNotes';
import PrettyError from '../../../decorators/PrettyError';
import DisplayError from '../../../decorators/DisplayError';
import { Broad as BaseBroad } from '../../Details/Broad';
import BaseTextarea from '../../../helpers/Textarea';
import { InputProps } from '../../../helpers/AutoComplete';
import { createIndustryErrorMessage } from '../../../../utils/industryTagsHelper';
import { TableFiles } from '../../Files/TableFiles';
import { Card } from '../../../helpers/Card';

import style from './style.module.css';

const Broad = PrettyError(DisplayError(BaseBroad));
const Textarea = PrettyError(DisplayError(BaseTextarea));
const Input = PrettyError(DisplayError(BaseInput));

const Marketing = ({
  contactsProps,
  companyInfo: company,
  buyerType,
  dealNotes,
  details,
  detailsCallbacks: callbacks,
  onChange,
  onEditEvent,
  onEnterDealNotes,
  onLeaveDealNotes,
  canEditData,
  hasPermissions,
  fetchEmailTemplates,
  findDirectors,
  emailTemplates,
  error,
  businessModels,
  industryCategories,
  onClick,
  onErrorClose,
  suggestResearcher,
  onDeleteFile,
  onFileUpload,
  files,
}) => {
  const inputSubmitRef = useRef();

  const handleOpenFileDialog = () => {
    if (inputSubmitRef) {
      inputSubmitRef.current.click();
    }
  };

  const buyerDealNotesProps = {
    buyerDealNotes: dealNotes,
    onEditEvent,
    onMouseEnter: onEnterDealNotes,
    onMouseLeave: onLeaveDealNotes,
  };

  const revenClassName = classNames('col-xs-6', {
    hide: /financial/i.test(buyerType),
  });

  const errorMessage = createIndustryErrorMessage(error);

  const disabled = !canEditData || !hasPermissions;

  return (
    <div className={classNames(style.host, 'tab-pane active tab-target')} id="buy-slide-1">
      <div className="col-xs-7 full-height deal-notes">
        {hasPermissions && <BuyerDealNotes {...buyerDealNotesProps} />}
      </div>
      <div className={classNames(style.side, 'col-xs-5 full-height')}>
        <div className={classNames(style.side, 'row full-height')}>
          <div className={classNames(style.sidebar, 'flexItem full-height')}>
            <div className="col-md-12">
              <CompanyContacts
                {...contactsProps}
                canEditData={canEditData}
                companyInfo={company}
                emailTemplates={emailTemplates}
                fetchEmailTemplates={fetchEmailTemplates}
                findDirectors={findDirectors}
                hasPermissions={hasPermissions}
              />
            </div>

            <Card as="section" className={style.card}>
              <form className={classNames(style.form, 'row')}>
                <div className="col-xs-6">
                  <FormGroup>
                    <Textarea
                      className="pb6"
                      disabled={disabled}
                      label="Buyer Description"
                      name="buyerDescription"
                      onChange={onChange}
                      placeholder="Buyer Description"
                      root={company}
                      rows="5"
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className="col-xs-6">
                  <div className="row">
                    <div className="col-xs-6">
                      <FormGroup>
                        <label className="field-label">Last Researcher</label>
                        <InputProps
                          className="form-control input-sm"
                          disabled={disabled}
                          placeholder="Last Researcher"
                        >
                          {suggestResearcher}
                        </InputProps>
                      </FormGroup>
                    </div>
                    <div className="col-xs-6">
                      <FormGroup>
                        <Input
                          className="input-sm"
                          disabled={disabled}
                          label="Last Researched"
                          name="buyerLastResearchedDate"
                          onChange={onChange}
                          placeholder="Last Researched Date"
                          root={company}
                          type="date"
                          deep
                          showLabel
                        />
                      </FormGroup>
                    </div>
                  </div>
                  <FormGroup>
                    <Textarea
                      disabled={disabled}
                      label="Research Notes"
                      name="buyerResearchNotes"
                      onChange={onChange}
                      placeholder="Buyer Research Notes"
                      root={company}
                      rows="4"
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
              </form>
              <CompanyHarvcoTags
                {...callbacks}
                canEditData={canEditData && hasPermissions}
                data={details.get('harvcoTags')}
                disabled={disabled}
              />

              <div className={style.broad}>
                <Broad
                  businessModels={businessModels}
                  data={industryCategories}
                  deprecatedIndustries={[]}
                  disabled={disabled}
                  error={errorMessage}
                  name="industryCategories"
                  onClick={onClick}
                  onErrorClose={onErrorClose}
                  canDisplayError
                />
              </div>

              <form className={classNames(style.form, 'row')}>
                <div className={revenClassName}>
                  <FormGroup>
                    <Input
                      className="input-sm"
                      disabled={disabled}
                      label="Revenue"
                      name="revenue"
                      onChange={onChange}
                      placeholder="Revenue"
                      root={company}
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className={revenClassName}>
                  <FormGroup>
                    <Input
                      className="input-sm"
                      disabled={disabled}
                      label="Employees"
                      name="employees"
                      onChange={onChange}
                      placeholder="Employees"
                      root={company}
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
              </form>
            </Card>

            <div className={style.files}>
              <div className={classNames(style.accordion, 'panel panel-default')}>
                <div className="panel-heading" data-target="#acc-slide" data-toggle="collapse">
                  <h4 className="panel-title">Files</h4>
                </div>
                <div className="panel-collapse collapse" id="acc-slide">
                  <div className="panel-body">
                    <form onSubmit={onFileUpload}>
                      <TableFiles
                        canEditData={canEditData && hasPermissions}
                        files={files}
                        onDeleteFile={onDeleteFile}
                        onUploadFile={handleOpenFileDialog}
                      />
                      <button ref={inputSubmitRef} className="hidden" type="submit" />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Marketing);
